import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Tooltip
} from '@mui/material';
import { Add, Delete, Edit, GetApp, Link as LinkIcon, Visibility } from '@mui/icons-material';
import dayjs from 'dayjs';
import axios from 'axios';
import { ROLES } from '../../constants/roles';
import { downloadUploadedFile, previewUploadedFile } from '../../utils/certificateTrainingFiles';

const TRAINING_TYPES = [
  { value: 'REQUIRED', label: 'Required' },
  { value: 'OPTIONAL', label: 'Optional' },
  { value: 'RECURRING', label: 'Recurring' },
  { value: 'CERTIFICATION', label: 'Certification' }
];

const formatDate = (value) => (value ? dayjs(value).format('YYYY-MM-DD') : 'N/A');

const getEndpointBase = (mode, entityId) => {
  if (mode === 'user') {
    return `/api/users/${entityId}/trainings`;
  }
  return `/api/users/onboard/${entityId}/trainings`;
};

const TrainingManagement = ({ entityId, mode = 'onboarding', readOnly = false, userRole = null }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    trainingId: '',
    trainingName: '',
    trainingType: TRAINING_TYPES[0].value,
    acquiredDate: dayjs().format('YYYY-MM-DD'),
    performedOnDate: '',
    expiryDate: '',
    periodicityMonths: '',
    hasNoExpiry: false,
    reminderDates: [],
    reminderDateInput: '',
    certificateUrl: '',
    remarks: '',
    file: null
  });

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(getEndpointBase(mode, entityId));
      setItems(response.data || []);
    } catch (err) {
      console.error('Failed to load trainings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, mode]);

  const resetForm = () => {
    setFormData({
      trainingId: '',
      trainingName: '',
      trainingType: TRAINING_TYPES[0].value,
      acquiredDate: dayjs().format('YYYY-MM-DD'),
      performedOnDate: '',
      expiryDate: '',
      periodicityMonths: '',
      hasNoExpiry: false,
      reminderDates: [],
      reminderDateInput: '',
      certificateUrl: '',
      remarks: '',
      file: null
    });
    setEditItem(null);
    setError('');
  };

  const openDialog = (training = null) => {
    if (training) {
      setEditItem(training);
      setFormData({
        trainingId: training.trainingId,
        trainingName: training.trainingName,
        trainingType: training.trainingType || TRAINING_TYPES[0].value,
        acquiredDate: formatDate(training.acquiredDate),
        performedOnDate: training.performedOnDate ? formatDate(training.performedOnDate) : '',
        expiryDate: training.expiryDate ? formatDate(training.expiryDate) : '',
        periodicityMonths: training.periodicityMonths != null ? String(training.periodicityMonths) : '',
        hasNoExpiry: training.hasNoExpiry,
        reminderDates: (training.reminderDates || []).map((d) => formatDate(d)).filter((d) => d && d !== 'N/A'),
        reminderDateInput: '',
        certificateUrl: training.certificateUrl || '',
        remarks: training.remarks || '',
        file: null
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    resetForm();
  };

  const handleChange = (field) => (event) => {
    const value = field === 'hasNoExpiry' ? event.target.checked : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (event) => {
    setFormData((prev) => ({ ...prev, file: event.target.files?.[0] || null }));
  };

  const handleSubmit = async () => {
    try {
      setError('');
      if (!editItem) {
        // Frontend validation
        if (!formData.trainingId || !formData.trainingId.trim()) {
          setError('Training ID is required.');
          return;
        }
        if (!formData.trainingName || !formData.trainingName.trim()) {
          setError('Training Name is required.');
          return;
        }
        if (!formData.acquiredDate) {
          setError('Acquired Date is required.');
          return;
        }

        const hasExpiry = !formData.hasNoExpiry;
        if (hasExpiry) {
          const months = parseInt(formData.periodicityMonths, 10);
          if (!Number.isFinite(months) || months <= 0) {
            setError('Periodicity (in months) is required and must be a whole number greater than 0 when training expires.');
            return;
          }
          if (!formData.expiryDate) {
            setError('Expiry Date could not be calculated. Please check acquired date and periodicity.');
            return;
          }
          if (!formData.reminderDates || formData.reminderDates.length === 0) {
            setError('At least one reminder date is required when training expires.');
            return;
          }
        }

        const data = new FormData();
        if (formData.file) {
          data.append('file', formData.file);
        }
        data.append('trainingId', formData.trainingId.trim());
        data.append('trainingName', formData.trainingName.trim());
        data.append('trainingType', formData.trainingType || 'OTHER');
        data.append('acquiredDate', formData.acquiredDate);
        if (formData.performedOnDate) {
          data.append('performedOnDate', formData.performedOnDate);
        }
        if (!formData.hasNoExpiry && formData.expiryDate) {
          data.append('expiryDate', formData.expiryDate);
        }
        if (!formData.hasNoExpiry && formData.periodicityMonths) {
          data.append('periodicityMonths', String(parseInt(formData.periodicityMonths, 10)));
        }
        // Convert boolean to string for FormData
        data.append('hasNoExpiry', formData.hasNoExpiry ? 'true' : 'false');
        if (formData.certificateUrl) {
          data.append('certificateUrl', formData.certificateUrl.trim());
        }
        if (formData.remarks) {
          data.append('remarks', formData.remarks.trim());
        }
        data.append(
          'reminderDates',
          JSON.stringify(formData.hasNoExpiry ? [] : (formData.reminderDates || []))
        );
        await axios.post(getEndpointBase(mode, entityId), data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        const isSuperAdmin = userRole === ROLES.SUPER_ADMIN;
        // For updates, allow file replacement
        if (formData.file) {
          const data = new FormData();
          data.append('file', formData.file);
          // SUPER_ADMIN can modify all fields
          if (isSuperAdmin) {
            data.append('trainingId', formData.trainingId.trim());
            data.append('trainingName', formData.trainingName.trim());
            data.append('trainingType', formData.trainingType || 'OTHER');
            data.append('acquiredDate', formData.acquiredDate);
          }
          // Always send performedOnDate (even if empty) so it can be cleared
          data.append('performedOnDate', formData.performedOnDate || '');
          data.append('expiryDate', formData.hasNoExpiry ? '' : (formData.expiryDate || ''));
          if (!formData.hasNoExpiry && formData.periodicityMonths) {
            data.append('periodicityMonths', String(parseInt(formData.periodicityMonths, 10)));
          }
          data.append('hasNoExpiry', formData.hasNoExpiry);
          if (formData.certificateUrl) {
            data.append('certificateUrl', formData.certificateUrl);
          }
          if (formData.remarks) {
            data.append('remarks', formData.remarks);
          }
          data.append(
            'reminderDates',
            JSON.stringify(formData.hasNoExpiry ? [] : (formData.reminderDates || []))
          );
          await axios.put(`${getEndpointBase(mode, entityId)}/${editItem._id}`, data, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } else {
          if (!formData.hasNoExpiry && (!formData.reminderDates || formData.reminderDates.length === 0)) {
            setError('At least one reminder date is required when training expires.');
            return;
          }
          const updateData = {
            expiryDate: formData.hasNoExpiry ? null : formData.expiryDate,
            periodicityMonths: formData.hasNoExpiry || !formData.periodicityMonths
              ? undefined
              : parseInt(formData.periodicityMonths, 10),
            hasNoExpiry: formData.hasNoExpiry,
            reminderDates: formData.hasNoExpiry ? [] : (formData.reminderDates || []),
            certificateUrl: formData.certificateUrl,
            remarks: formData.remarks
          };
          if (formData.performedOnDate) {
            updateData.performedOnDate = formData.performedOnDate;
          } else {
            updateData.performedOnDate = null;
          }
          // SUPER_ADMIN can modify all fields
          if (isSuperAdmin) {
            updateData.trainingId = formData.trainingId.trim();
            updateData.trainingName = formData.trainingName.trim();
            updateData.trainingType = formData.trainingType || 'OTHER';
            updateData.acquiredDate = formData.acquiredDate;
          }
          await axios.put(`${getEndpointBase(mode, entityId)}/${editItem._id}`, updateData);
        }
      }
      closeDialog();
      fetchItems();
      } catch (err) {
      console.error('Training save error:', err);
      console.error('Error response:', err.response?.data);
      // Show detailed error message
      if (err.response?.data?.errors) {
        const errorMessages = err.response.data.errors.map(e => `${e.param}: ${e.msg}`).join(', ');
        setError(`Validation failed: ${errorMessages}`);
      } else {
        setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save training.');
      }
    }
  };

  // Auto-calculate expiry date when periodicity months and acquired date are set, and training has expiry
  React.useEffect(() => {
    if (formData.hasNoExpiry) {
      return;
    }
    const months = parseInt(formData.periodicityMonths, 10);
    if (!Number.isFinite(months) || months <= 0) {
      return;
    }
    if (!formData.acquiredDate) {
      return;
    }
    const nextExpiry = dayjs(formData.acquiredDate).add(months, 'month').format('YYYY-MM-DD');
    setFormData((prev) => {
      if (prev.expiryDate === nextExpiry) {
        return prev;
      }
      return { ...prev, expiryDate: nextExpiry };
    });
  }, [formData.acquiredDate, formData.periodicityMonths, formData.hasNoExpiry]);

  const handleDelete = async (training) => {
    if (!window.confirm('Delete this training entry?')) return;
    try {
      await axios.delete(`${getEndpointBase(mode, entityId)}/${training._id}`);
      fetchItems();
    } catch (err) {
      console.error('Training delete error:', err);
    }
  };

  return (
    <Box mt={4}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Trainings</Typography>
        {!readOnly && (
          <Button variant="outlined" startIcon={<Add />} onClick={() => openDialog()}>
            Add Training
          </Button>
        )}
      </Stack>

      {loading ? (
        <Typography>Loading trainings...</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Acquired</TableCell>
              <TableCell>Expiry</TableCell>
              <TableCell>Reminder Dates</TableCell>
              <TableCell>File</TableCell>
              <TableCell>Remarks</TableCell>
              {!readOnly && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((training) => {
              const isExpired = training.expiryDate && dayjs(training.expiryDate).isBefore(dayjs(), 'day');
              const isExpiringSoon = training.expiryDate && dayjs(training.expiryDate).diff(dayjs(), 'day') <= 60 && !isExpired;
              return (
                <TableRow key={training._id} hover>
                  <TableCell>{training.trainingId}</TableCell>
                  <TableCell>{training.trainingName}</TableCell>
                  <TableCell>{training.trainingType || '—'}</TableCell>
                  <TableCell>{formatDate(training.acquiredDate)}</TableCell>
                  <TableCell>
                    {training.hasNoExpiry ? (
                      <Chip label="No Expiry" size="small" />
                    ) : (
                      <Chip
                        label={formatDate(training.expiryDate)}
                        size="small"
                        color={isExpired ? 'error' : (isExpiringSoon ? 'warning' : 'default')}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {training.hasNoExpiry ? (
                      '—'
                    ) : (training.reminderDates || []).length ? (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {(training.reminderDates || []).map((d) => (
                          <Chip key={String(d)} label={formatDate(d)} size="small" variant="outlined" />
                        ))}
                      </Stack>
                    ) : (
                      <Chip label="Missing" size="small" color="warning" />
                    )}
                  </TableCell>
                  <TableCell>
                    {training.fileUrl ? (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                          size="small"
                          startIcon={<Visibility />}
                          onClick={async () => {
                            try {
                              await previewUploadedFile({
                                mode,
                                entityId,
                                resource: 'trainings',
                                itemId: training._id,
                                mimeType: training.mimeType
                              });
                            } catch (error) {
                              console.error('Preview error:', error);
                              alert(error.message || 'Failed to preview file. Please try again.');
                            }
                          }}
                          variant="outlined"
                        >
                          Preview
                        </Button>
                        <Button
                          size="small"
                          startIcon={<GetApp />}
                          onClick={async () => {
                            try {
                              await downloadUploadedFile({
                                mode,
                                entityId,
                                resource: 'trainings',
                                itemId: training._id,
                                fileName: training.fileName || 'training',
                                mimeType: training.mimeType
                              });
                            } catch (error) {
                              console.error('Download error:', error);
                              alert('Failed to download file. Please try again.');
                            }
                          }}
                          variant="outlined"
                        >
                          Download
                        </Button>
                      </Stack>
                    ) : training.certificateUrl ? (
                      <Button
                        size="small"
                        startIcon={<LinkIcon />}
                        href={training.certificateUrl}
                        target="_blank"
                        variant="outlined"
                      >
                        View URL
                      </Button>
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>{training.remarks || '—'}</TableCell>
                  {!readOnly && (
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openDialog(training)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(training)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={readOnly ? 8 : 9} align="center">No trainings recorded.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editItem ? 'Update Training' : 'Add Training'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Training ID"
              value={formData.trainingId}
              onChange={handleChange('trainingId')}
              fullWidth
              required
              disabled={!!editItem && userRole !== ROLES.SUPER_ADMIN}
              error={!editItem && !formData.trainingId?.trim()}
              helperText={!editItem && !formData.trainingId?.trim() ? 'Training ID is required' : ''}
            />
            <TextField
              label="Training Name"
              value={formData.trainingName}
              onChange={handleChange('trainingName')}
              fullWidth
              required
              disabled={!!editItem && userRole !== ROLES.SUPER_ADMIN}
              error={!editItem && !formData.trainingName?.trim()}
              helperText={!editItem && !formData.trainingName?.trim() ? 'Training Name is required' : ''}
            />
            <TextField
              select
              label="Training Type"
              value={formData.trainingType}
              onChange={handleChange('trainingType')}
              fullWidth
            >
              {TRAINING_TYPES.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Acquired Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={formData.acquiredDate}
              onChange={handleChange('acquiredDate')}
              fullWidth
              required
              disabled={!!editItem && userRole !== ROLES.SUPER_ADMIN}
              error={!editItem && !formData.acquiredDate}
              helperText={!editItem && !formData.acquiredDate ? 'Acquired Date is required' : 'Date when the training was acquired or completed'}
            />
            <TextField
              label="Performed On Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={formData.performedOnDate}
              onChange={handleChange('performedOnDate')}
              fullWidth
              helperText="Date when the training was actually performed (can be before or after acquired date)"
            />
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                label="Expiry Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.hasNoExpiry ? '' : formData.expiryDate}
                onChange={userRole === ROLES.SUPER_ADMIN ? handleChange('expiryDate') : undefined}
                fullWidth
                disabled={userRole !== ROLES.SUPER_ADMIN}
                helperText={formData.hasNoExpiry ? '' : (userRole === ROLES.SUPER_ADMIN ? 'Manually set expiry date' : 'Auto-calculated from acquired date and periodicity')}
              />
              <TextField
                select
                label="Has Expiry?"
                value={formData.hasNoExpiry ? 'no' : 'yes'}
                onChange={(event) => {
                  const hasExpiry = event.target.value === 'yes';
                  setFormData((prev) => ({
                    ...prev,
                    hasNoExpiry: !hasExpiry,
                    periodicityMonths: hasExpiry ? prev.periodicityMonths : '',
                    expiryDate: hasExpiry ? prev.expiryDate : '',
                    reminderDates: hasExpiry ? prev.reminderDates : []
                  }));
                }}
                sx={{ width: 150 }}
              >
                <MenuItem value="yes">Expires</MenuItem>
                <MenuItem value="no">No Expiry</MenuItem>
              </TextField>
            </Stack>
            {!formData.hasNoExpiry && (
              <>
                <TextField
                  label="Periodicity (in months)"
                  type="number"
                  value={formData.periodicityMonths}
                  onChange={handleChange('periodicityMonths')}
                  fullWidth
                  required
                  inputProps={{ min: 1, step: 1 }}
                  helperText="Enter a whole number of months; expiry date will be auto-calculated."
                />
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Reminder Dates (required)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    Dates on or before expiry when renewal reminders should be sent.
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <TextField
                      type="date"
                      size="small"
                      label="Add reminder date"
                      InputLabelProps={{ shrink: true }}
                      value={formData.reminderDateInput || ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, reminderDateInput: e.target.value }))}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      variant="outlined"
                      onClick={() => {
                        const value = (formData.reminderDateInput || '').trim();
                        if (!value) return;
                        setFormData((prev) => {
                          if ((prev.reminderDates || []).includes(value)) return { ...prev, reminderDateInput: '' };
                          return {
                            ...prev,
                            reminderDates: [...(prev.reminderDates || []), value].sort(),
                            reminderDateInput: ''
                          };
                        });
                      }}
                    >
                      Add
                    </Button>
                  </Stack>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {(formData.reminderDates || []).map((d) => (
                      <Chip
                        key={d}
                        label={d}
                        size="small"
                        onDelete={() =>
                          setFormData((prev) => ({
                            ...prev,
                            reminderDates: (prev.reminderDates || []).filter((x) => x !== d)
                          }))
                        }
                      />
                    ))}
                    {(formData.reminderDates || []).length === 0 && (
                      <Typography variant="body2" color="error">
                        At least one reminder date is required.
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </>
            )}
            {editItem && (editItem.fileUrl || editItem.certificateUrl) && (
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>Current File/URL:</Typography>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  {editItem.fileUrl && (
                    <>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Visibility />}
                        onClick={async () => {
                          try {
                            await previewUploadedFile({
                              mode,
                              entityId,
                              resource: 'trainings',
                              itemId: editItem._id,
                              mimeType: editItem.mimeType
                            });
                          } catch (error) {
                            console.error('Preview error:', error);
                            alert(error.message || 'Failed to preview file. Please try again.');
                          }
                        }}
                      >
                        Preview File
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<GetApp />}
                        onClick={async () => {
                          try {
                            await downloadUploadedFile({
                              mode,
                              entityId,
                              resource: 'trainings',
                              itemId: editItem._id,
                              fileName: editItem.fileName || 'training',
                              mimeType: editItem.mimeType
                            });
                          } catch (error) {
                            console.error('Download error:', error);
                            alert('Failed to download file. Please try again.');
                          }
                        }}
                      >
                        Download File ({editItem.fileName || 'file'})
                      </Button>
                    </>
                  )}
                  {editItem.certificateUrl && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<LinkIcon />}
                      href={editItem.certificateUrl}
                      target="_blank"
                    >
                      Open Certificate URL
                    </Button>
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  To replace the file, upload a new one below
                </Typography>
              </Box>
            )}
            <Button variant="outlined" component="label">
              {formData.file ? formData.file.name : (editItem ? 'Replace File (Optional)' : 'Select File (Optional)')}
              <input type="file" hidden onChange={handleFileChange} accept=".pdf,image/*" />
            </Button>
            <TextField
              label="Certificate URL (Optional)"
              value={formData.certificateUrl}
              onChange={handleChange('certificateUrl')}
              fullWidth
              helperText={editItem ? "Update the certificate URL if needed" : "Provide a URL to the training certificate if not uploading a file"}
            />
            <TextField
              label="Remarks"
              value={formData.remarks}
              onChange={handleChange('remarks')}
              fullWidth
              multiline
              minRows={2}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editItem ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingManagement;
